import { ConsentPermissionDescriptor, UserPiiPermissions } from '@/types'

/**
 * Shared consent helpers for every surface that asks for, or manages, a partner's access: the
 * embedded-SSO consent screen, the OAuth consent screen, the silent auto-grant, and Connected Apps.
 *
 * ## Driven by the server's descriptors, keyed by the server's keys
 *
 * What a partner asks for is data: the server sends one descriptor per consent, in the order to
 * show them, with its own label and whether it is required. Nothing here enumerates keys, so a
 * consent the server adds (`benefits.view`) or starts requiring renders and is answered without a
 * web release. The answer goes back as `grants`, keyed the same way.
 *
 * ## The one table left is a fallback for older servers
 *
 * A server from before `grants` existed only understands the fixed ten-field `permissions` object,
 * and only reports what a member granted in that shape. {@link LEGACY_PERMISSION_FIELDS} maps
 * between the two for exactly that case, and nothing else reads it: a key it does not know is still
 * rendered and still answered in `grants` — it just has no legacy field to mirror into.
 *
 * Kept outside the component files so they stay component-only (React Fast Refresh).
 */

/** Legacy key → fixed-field mapping. Fallback only; see the module comment. */
const LEGACY_PERMISSION_FIELDS: Record<string, keyof UserPiiPermissions> = {
  'pii.name': 'name',
  'pii.partial_name': 'partialName',
  'pii.email': 'email',
  'pii.age': 'age',
  'pii.gender': 'gender',
  'pii.location': 'location',
  'pii.ratings': 'ratings',
  'pii.birth_year': 'birthYear',
  'pii.profile_photo': 'profilePhoto',
  'pii.dupr_id': 'duprId',
}

/** `required` when the server sends it; an older server only says so through `category`. */
export const isRequiredPermission = (descriptor: ConsentPermissionDescriptor): boolean =>
  descriptor.required ?? descriptor.category === 'REQUIRED'

/**
 * The descriptors a screen shows, in server order.
 *
 * The server no longer sends BLOCKED ones, but a screen that rendered one would offer the member a
 * toggle for something the partner may never have — so they are still dropped here, defensively.
 */
export const visiblePermissions = (descriptors: ConsentPermissionDescriptor[]): ConsentPermissionDescriptor[] =>
  descriptors.filter((descriptor) => descriptor.category !== 'BLOCKED')

/** An older server's fixed-field answer, re-keyed the way the rest of this module reads grants. */
export const grantsFromLegacyPermissions = (
  permissions: UserPiiPermissions | null | undefined
): Record<string, boolean> => {
  const grants: Record<string, boolean> = {}
  if (!permissions) return grants
  Object.entries(LEGACY_PERMISSION_FIELDS).forEach(([key, field]) => {
    grants[key] = !!permissions[field]
  })
  return grants
}

/**
 * What the member has already granted, by key: the server's `grants` when it sends them, otherwise
 * the legacy fixed-field object translated through the fallback table.
 */
export const resolveExistingGrants = (
  grants: Record<string, boolean> | null | undefined,
  legacyPermissions: UserPiiPermissions | null | undefined
): Record<string, boolean> => (grants ? grants : grantsFromLegacyPermissions(legacyPermissions))

/**
 * The starting position of every toggle: required ones on, optional ones as the member last left
 * them (off when they never answered).
 */
export const buildInitialToggles = (
  descriptors: ConsentPermissionDescriptor[],
  existingGrants: Record<string, boolean>
): Record<string, boolean> => {
  const toggles: Record<string, boolean> = {}
  visiblePermissions(descriptors).forEach((descriptor) => {
    toggles[descriptor.key] = isRequiredPermission(descriptor) ? true : !!existingGrants[descriptor.key]
  })
  return toggles
}

/**
 * The key-based answer: one entry for every descriptor shown, required ones forced on.
 *
 * Every shown key is present, including the ones switched off, so the server reads an explicit
 * answer for each question it asked rather than inferring one from silence.
 */
export const buildGrants = (
  descriptors: ConsentPermissionDescriptor[],
  toggles: Record<string, boolean>
): Record<string, boolean> => {
  const grants: Record<string, boolean> = {}
  visiblePermissions(descriptors).forEach((descriptor) => {
    grants[descriptor.key] = isRequiredPermission(descriptor) || !!toggles[descriptor.key]
  })
  return grants
}

/**
 * The same answer in the fixed-field form an older server reads. Keys with no legacy field are
 * simply not mirrored — `grants` carries them.
 */
export const buildPermissionsPayload = (grants: Record<string, boolean>): UserPiiPermissions => {
  const permissions: UserPiiPermissions = {
    name: false,
    partialName: false,
    email: false,
    age: false,
    gender: false,
    location: false,
    ratings: false,
    birthYear: false,
    profilePhoto: false,
    duprId: false,
  }
  Object.entries(grants).forEach(([key, value]) => {
    const field = LEGACY_PERMISSION_FIELDS[key]
    if (field) {
      permissions[field] = value
    }
  })
  return permissions
}

/** The slice of i18next a label lookup needs; `useTranslation()`'s `i18n` satisfies it. */
export interface PermissionLabelLocalizer {
  t: (key: string) => string
  exists: (key: string) => boolean
}

/**
 * The row's label: our own translation of `labelKey` when this app ships one, otherwise the
 * server's `label`, which is always sent for exactly that reason.
 */
export const permissionLabel = (
  descriptor: ConsentPermissionDescriptor,
  localizer?: PermissionLabelLocalizer
): string => {
  const key = descriptor.labelKey
  if (key && localizer?.exists(key)) return localizer.t(key)
  return descriptor.label
}
